import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import type { LunarPaymentDto, LunarPaymentsResponseDto } from '@lunary/api-client'
import { HBars } from '../../shared/ui/charts/hbars'
import { formatNumber } from '../../shared/ui/charts/scale'
import { CURRENCY_LABELS, PAYMENT_STATUS_LABELS, PROVIDER_LABELS, formatDateTime, shortId } from './labels'
import styles from './lunars.module.css'
import { RefundDialog } from './refund-dialog'

function statusClass(status: string, credited: boolean): string {
  if (status === 'refunded' || status === 'failed') return 'badge danger'
  if (status === 'paid' && credited) return 'badge ok'
  return 'badge'
}

function journalNote(status: string, credited: boolean): string {
  if (credited) return 'начислено'
  return status === 'paid' || status === 'refunded' ? 'нет записи' : '—'
}

export function PaymentsTab({ data }: { data: LunarPaymentsResponseDto }) {
  const [refunding, setRefunding] = useState<LunarPaymentDto | null>(null)
  return (
    <div className={styles.stack}>
      <div className="card-panel">
        <p className={styles.caption}>Провайдеры за период: Stars в XTR, остальные в рублях</p>
        <div className={styles.scroll}>
          <table className="users-table">
            <thead>
              <tr>
                <th>Провайдер</th>
                <th>Оплат</th>
                <th>Сумма</th>
                <th>Дошли до оплаты</th>
                <th>Ждут больше часа</th>
                <th>Ошибки</th>
                <th>Возвраты</th>
              </tr>
            </thead>
            <tbody>
              {data.providers.map((p) => {
                const cur = CURRENCY_LABELS[p.currency] ?? p.currency
                return (
                  <tr key={`${p.provider}-${p.currency}`}>
                    <td>{PROVIDER_LABELS[p.provider] ?? p.provider}</td>
                    <td>{formatNumber(p.paid_count)}</td>
                    <td>
                      {formatNumber(p.paid_sum)} {cur}
                    </td>
                    <td>{p.created_count > 0 ? `${Math.round((p.paid_count / p.created_count) * 100)}%` : '—'}</td>
                    <td>{formatNumber(p.stuck_count)}</td>
                    <td>{formatNumber(p.failed_count)}</td>
                    <td>
                      {formatNumber(p.refunded_count)}
                      {p.refunded_count > 0 && ` · ${formatNumber(p.refunded_sum)} ${cur}`}
                    </td>
                  </tr>
                )
              })}
              {data.providers.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted">
                    Платежей за период нет.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className={styles.split}>
        <div className="card-panel">
          <p className={styles.caption}>Пакеты (оплаченные)</p>
          <HBars
            label="Пакеты"
            color="var(--series-4)"
            items={data.packs.map((p) => ({ key: p.pack_id, label: `${formatNumber(p.lunars)} лунаров`, value: p.count }))}
          />
        </div>
        <div className="card-panel">
          <p className={styles.caption}>Последние оплаты</p>
          <div className={styles.scroll}>
            <table className="users-table">
              <thead>
                <tr>
                  <th>Время</th>
                  <th>Провайдер</th>
                  <th>Пакет</th>
                  <th>Пользователь</th>
                  <th>Статус</th>
                  <th>Журнал</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.recent.map((r) => (
                  <tr key={r.id}>
                    <td>{formatDateTime(r.created_at)}</td>
                    <td>{PROVIDER_LABELS[r.provider] ?? r.provider}</td>
                    <td>{formatNumber(r.lunars)}</td>
                    <td className={styles.mono}>
                      <Link to="/users/$userId" params={{ userId: r.user_id }}>
                        {shortId(r.user_id)}
                      </Link>
                    </td>
                    <td>
                      <span className={statusClass(r.status, r.credited)}>{PAYMENT_STATUS_LABELS[r.status] ?? r.status}</span>
                    </td>
                    <td>{journalNote(r.status, r.credited)}</td>
                    <td>
                      {r.provider === 'yookassa' && r.status === 'paid' && (
                        <button type="button" className="secondary" onClick={() => setRefunding(r)}>
                          Вернуть
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <RefundDialog payment={refunding} onClose={() => setRefunding(null)} />
    </div>
  )
}
